/**
 * When the client opened this document, and how many times.
 *
 * `Invoice.viewedAt` used to be the whole answer, and it answers once: the
 * first visit, never rewritten. That is the wrong shape for the conversation
 * that actually happens - a client says the invoice never arrived, and the
 * useful reply is "it was opened three times, the last one on Tuesday", not
 * "at some point somebody looked". So the API now files every opening and this
 * card reads the list.
 *
 * ## What is deliberately NOT on it
 *
 * No "not opened yet" state. The document goes out as a mail attachment as well
 * as a link, so an empty log means we have no record, never "nobody looked -
 * chase them", and a card printing the second sentence would send operators
 * after clients who paid last week. The card appears only when there is
 * something to report, exactly like `PortalDecisionCard` beside it.
 *
 * No address. The API stores the visitor's IP and user agent and does not
 * return them: they exist so that one disputed visit can be examined
 * deliberately, not so every screen quietly prints where a client was sitting.
 *
 * The caveat under the list is not decoration. A mail security scan following
 * the link lands in this log exactly like a client would, so a row means "the
 * address was opened" and an operator leaning on it in front of a customer
 * needs to know that.
 */
import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { listDocViews, type DocViewHistory, type SalesShareType } from "../../../lib/sales";
import { Card, CardHead, fmtDateTime } from "../../ui";

export function DocViewsCard({
  type,
  id,
  /** The old first-visit stamp, for documents opened before this log existed. */
  fallbackViewedAt,
}: {
  type: SalesShareType;
  id: string;
  fallbackViewedAt?: string | null;
}) {
  const t = useT();
  const [history, setHistory] = useState<DocViewHistory | null>(null);

  useEffect(() => {
    let live = true;
    listDocViews(type, id)
      .then((h) => { if (live) setHistory(h); })
      .catch(() => { if (live) setHistory({ views: [], total: 0, firstAt: null, lastAt: null }); });
    return () => { live = false; };
  }, [type, id]);

  // Still loading, or nothing to say. Both draw nothing rather than a spinner:
  // this card is a footnote on the document, and a placeholder for a card that
  // may never appear is worse than the wait.
  if (!history) return null;
  if (history.total === 0 && !fallbackViewedAt) return null;

  if (history.total === 0) {
    return (
      <Card className="mt-4 px-[26px] py-[22px]">
        <div className="crm-card-t mb-2.5 flex items-center gap-2">
          <Eye className="size-4 text-emerald-600" />
          {t("sales.docViews.title")}
        </div>
        <p className="crm-mut text-[13px] leading-relaxed">
          {t("sales.docViews.legacy", { when: fmtDateTime(fallbackViewedAt!) })}
        </p>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardHead
        title={
          <span className="flex items-center gap-2">
            <Eye className="size-4 text-emerald-600" />
            {t("sales.docViews.title")}
            <span className="crm-mut text-[12.5px]">{t("sales.docViews.count", { n: history.total })}</span>
          </span>
        }
        sub={t("sales.docViews.sub")}
      />

      <div className="px-[26px] pb-7 pt-1">
        <div className="crm-mut flex flex-wrap gap-x-5 gap-y-1 text-[13px] leading-relaxed">
          {history.lastAt ? <span>{t("sales.docViews.last", { when: fmtDateTime(history.lastAt) })}</span> : null}
          {history.firstAt && history.total > 1
            ? <span>{t("sales.docViews.first", { when: fmtDateTime(history.firstAt) })}</span>
            : null}
        </div>

        <ul className="mt-3 flex flex-col gap-1.5">
          {history.views.map((v) => (
            <li key={v.id} className="flex flex-wrap items-baseline gap-x-2.5 text-[13px]">
              <span className="crm-tnum">{fmtDateTime(v.createdAt)}</span>
              <span className="crm-mut text-[12px]">{t(`sales.docViews.source.${v.source}`)}</span>
            </li>
          ))}
        </ul>

        {/* Only when the list is shorter than the count, so the usual case says
            nothing about a cap that did not bite. */}
        {history.total > history.views.length ? (
          <p className="crm-mut mt-3 text-[12.5px] leading-relaxed">
            {t("sales.docViews.capped", { n: history.views.length })}
          </p>
        ) : null}

        <p className="crm-mut mt-3 text-[12.5px] leading-relaxed">{t("sales.docViews.notProof")}</p>
      </div>
    </Card>
  );
}
